import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal, Overline, Rule } from "@/components/site/Primitives";
import { ArticleCard } from "@/components/site/ArticleCard";
import { EmptyState } from "@/components/site/EmptyState";
import { Watermark } from "@/components/site/Primitives";

export default function Articles() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("search") || "");

  const category = params.get("category") || "";
  const tag = params.get("tag") || "";
  const search = params.get("search") || "";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    api.get("/public/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/articles", { params: { category, tag, search, page, per_page: 9 } });
      setData(data);
    } catch {
      setData({ items: [], total: 0, pages: 1, page: 1 });
    } finally {
      setLoading(false);
    }
  }, [category, tag, search, page]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  return (
    <>
      <Seo title="Articles" description="Insights on strategic planning, business management, risk, governance, financial perspectives and technology." />

      <section className="relative overflow-hidden border-b border-gold-500/20 bg-cream-200/40">
        <Watermark className="-right-6 top-2 text-[13rem]">Articles</Watermark>
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-10">
          <Overline>Insights &amp; Perspectives</Overline>
          <h1 className="mt-4 font-serif text-5xl font-light text-ink lg:text-6xl">Articles</h1>
          <Rule className="mt-6" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 lg:px-10">
        {/* Filters */}
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => update({ category: "" })}
              className={`rounded-none border px-4 py-2 font-sans text-sm transition-colors ${!category ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 text-ink/70 hover:border-gold-500"}`}
              data-testid="filter-category-all"
            >
              All Topics
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => update({ category: c.name })}
                className={`rounded-none border px-4 py-2 font-sans text-sm transition-colors ${category === c.name ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 text-ink/70 hover:border-gold-500"}`}
                data-testid={`filter-category-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); update({ search: searchInput }); }}
            className="flex items-center gap-2 border-b border-ink/20 pb-2"
          >
            <Search className="h-4 w-4 text-steel-500" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search articles…"
              className="w-full bg-transparent font-sans text-sm text-ink placeholder:text-steel-400 focus:outline-none lg:w-56"
              data-testid="articles-search-input"
            />
          </form>
        </div>

        {(tag || search) && (
          <p className="mb-8 font-sans text-sm text-steel-500">
            Showing results{tag && <> tagged “{tag}”</>}{search && <> for “{search}”</>}.{" "}
            <button onClick={() => setParams(new URLSearchParams())} className="text-gold-600 gf-link">Clear filters</button>
          </p>
        )}

        {loading ? (
          <p className="py-20 text-center font-sans text-steel-500">Loading…</p>
        ) : data.items.length === 0 ? (
          <div className="py-10">
            <EmptyState icon={FileText} title="No articles yet" body="There are no published articles matching this view. Please check back soon — new insights are added regularly." />
          </div>
        ) : (
          <>
            <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
              {data.items.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.04}><ArticleCard article={a} /></Reveal>
              ))}
            </div>

            {data.pages > 1 && (
              <div className="mt-16 flex items-center justify-center gap-2" data-testid="pagination">
                <button
                  disabled={page <= 1}
                  onClick={() => update({ page: String(page - 1), category, tag, search })}
                  className="flex h-10 w-10 items-center justify-center border border-ink/15 text-ink disabled:opacity-30 hover:border-gold-500"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => update({ page: String(p), category, tag, search })}
                    className={`h-10 w-10 border font-sans text-sm ${p === page ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 text-ink hover:border-gold-500"}`}
                    data-testid={`pagination-page-${p}`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  disabled={page >= data.pages}
                  onClick={() => update({ page: String(page + 1), category, tag, search })}
                  className="flex h-10 w-10 items-center justify-center border border-ink/15 text-ink disabled:opacity-30 hover:border-gold-500"
                  data-testid="pagination-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
